import compactDisplays from "../constants/compactDisplay";
import defaultFictionNarrativeLabels from "../constants/defaultFictionNarrativeLabels";
import itemTypeIds from "../constants/itemTypeIds";
import narrativeNumberingOptions from "../constants/narrativeNumberingOptions";
import { REFERENCE_RELATES_TO } from "../constants/references";
import subwayDisplayOptions from "../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../constants/zoomRange";
import { getGregorianCalendarObject } from "../modules/definition/calendar/gregorianCalendarRaw";

const calendar = getGregorianCalendarObject();

export default {
  types: {
    byId: {
      genericNode: {
        enabled: true,
        canDelete: false,
        id: "genericNode",
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        isRelative: false,
        relativeUnits: "seconds"
      },
      event: {
        enabled: true,
        canDelete: false,
        id: "event",
        label: "Event",
        pluralLabel: "Events",
        references: [
          "witnessRef",
          "participantRef",
          "storyArcRef",
          "locationRef",
          "protagonistRef",
          "antagonistRef",
          "bookRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO
        ],
        properties: ["complete", "notes", "tension"],
        superTypes: ["event", "flashback", "backstory"],
        icon: "diamond",
        color: "iconYellow",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        isRelative: true,
        relativeUnits: "seconds",
        defaultSequenceType: true
      },
      narrativeFolder: {
        enabled: true,
        canDelete: false,
        id: "narrativeFolder",
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        references: ["witnessRef", "participantRef", "storyArcRef", "locationRef", "bookRef", REFERENCE_RELATES_TO],
        properties: ["complete", "notes"],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NF",
        isRelative: false,
        relativeUnits: "seconds",
        defaultNarrativeType: false,
        isNarrativeFolder: true
      },
      character: {
        enabled: true,
        canDelete: false,
        id: "character",
        label: "Character",
        pluralLabel: "Characters",
        references: ["bookRef", "birthplaceRef", "familyRef", "friendRef", "associateRef", REFERENCE_RELATES_TO],
        properties: [],
        superTypes: ["character", itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "person",
        color: "iconRed",
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PER",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        isRelative: false,
        relativeUnits: "seconds"
      },
      storyArc: {
        enabled: true,
        canDelete: false,
        id: "storyArc",
        label: "Story Arc",
        pluralLabel: "Story Arcs",
        references: ["protagonistRef", "antagonistRef", "bookRef", REFERENCE_RELATES_TO],
        properties: ["tension"],
        superTypes: ["storyArc"],
        icon: "star",
        color: "iconYellow",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "ARC",
        isRelative: false,
        relativeUnits: "seconds"
      },
      [itemTypeIds.LOCATION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: false,
        id: itemTypeIds.LOCATION_ITEM_TYPE_ID,
        label: "Location",
        pluralLabel: "Locations",
        references: ["bookRef", "causesRef", REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "map",
        color: "iconOrange",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "LOC",
        isRelative: false,
        relativeUnits: "seconds"
      },
      scene: {
        enabled: true,
        canDelete: false,
        id: "scene",
        label: "Scene",
        pluralLabel: "Scenes",
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "protagonistRef",
          "antagonistRef",
          "bookRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO
        ],
        properties: ["complete", "notes", "tension"],
        superTypes: ["event", "flashback", "backstory"],
        icon: "diamond",
        color: "iconOrange",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "SC",
        isRelative: false,
        relativeUnits: "seconds",
        defaultNarrativeType: true
      },
      flashback: {
        enabled: true,
        canDelete: false,
        id: "flashback",
        label: "Flashback",
        pluralLabel: "Flashbacks",
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "protagonistRef",
          "antagonistRef",
          "bookRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO
        ],
        properties: ["complete", "notes", "tension"],
        superTypes: ["event", "flashback", "backstory"],
        icon: "diamond",
        color: "iconGreen",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "FL",
        isRelative: false,
        relativeUnits: "seconds"
      },
      backstory: {
        enabled: true,
        canDelete: false,
        id: "backstory",
        label: "Backstory",
        pluralLabel: "Backstories",
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "protagonistRef",
          "antagonistRef",
          "bookRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO
        ],
        properties: ["complete", "notes", "tension"],
        superTypes: ["event", "flashback", "backstory"],
        icon: "diamond",
        color: "iconRed",
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "BAK",
        isRelative: false,
        relativeUnits: "seconds"
      },
      book: {
        enabled: true,
        canDelete: false,
        id: "book",
        label: "Book",
        pluralLabel: "Books",
        references: ["protagonistRef", "antagonistRef", "bookRef", REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "book",
        color: "iconPurple",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "BK",
        isRelative: false,
        relativeUnits: "seconds"
      }
    },
    allIds: [
      "event",
      "scene",
      "flashback",
      "backstory",
      "character",
      itemTypeIds.LOCATION_ITEM_TYPE_ID,
      "storyArc",
      "narrativeFolder",
      "book",
      "genericNode"
    ]
  },
  properties: {
    byId: {
      complete: {
        enabled: true,
        canDelete: false,
        id: "complete",
        key: "complete",
        label: "Complete",
        format: "boolean",
        allowed: null,
        multiple: false
      },
      notes: {
        enabled: true,
        canDelete: false,
        id: "notes",
        key: "notes",
        label: "Notes",
        format: "multitext",
        allowed: null,
        multiple: false
      },
      tension: {
        enabled: true,
        canDelete: false,
        id: "tension",
        key: null,
        label: "Tension",
        format: "percentage",
        allowed: null,
        multiple: false
      }
    },
    allIds: ["complete", "notes", "tension"]
  },
  references: {
    byId: {
      protagonistRef: {
        enabled: true,
        canDelete: false,
        id: "protagonistRef",
        key: null,
        label: "Protagonist",
        types: ["character"],
        icon: "circle",
        color: "lightTeal",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      locationRef: {
        enabled: true,
        canDelete: false,
        id: "locationRef",
        key: "locationRef",
        label: "Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "red",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      antagonistRef: {
        enabled: true,
        canDelete: false,
        id: "antagonistRef",
        key: null,
        label: "Antagonist",
        types: ["character"],
        icon: "circle",
        color: "red",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      birthplaceRef: {
        enabled: true,
        canDelete: false,
        id: "birthplaceRef",
        key: null,
        label: "Birthplace",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "apple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      participantRef: {
        enabled: true,
        canDelete: false,
        id: "participantRef",
        key: "participant",
        label: "Participant",
        types: ["character"],
        icon: "circle filled",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      associateRef: {
        enabled: true,
        canDelete: false,
        id: "associateRef",
        key: null,
        label: "Associate",
        types: ["character"],
        icon: "circle",
        color: "grey",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      seeAlsoRef: {
        enabled: true,
        canDelete: false,
        id: "seeAlsoRef",
        key: null,
        label: "See also",
        types: ["event", "flashback", "scene", "backstory"],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      witnessRef: {
        enabled: true,
        canDelete: false,
        id: "witnessRef",
        key: "witness",
        label: "Witness",
        types: ["character"],
        icon: "circle",
        color: "lightBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      storyArcRef: {
        enabled: true,
        canDelete: false,
        id: "storyArcRef",
        key: "story",
        label: "Story Arc",
        types: ["storyArc"],
        icon: "circle filled",
        color: "peach",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      friendRef: {
        enabled: true,
        canDelete: false,
        id: "friendRef",
        key: null,
        label: "Friend",
        types: ["character"],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      bookRef: {
        enabled: true,
        canDelete: false,
        id: "bookRef",
        key: null,
        label: "Book",
        types: ["book"],
        icon: "circle",
        color: "darkBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      causesRef: {
        enabled: true,
        canDelete: false,
        id: "causesRef",
        key: null,
        label: "Causes",
        types: ["event", "flashback", "scene", "backstory"],
        icon: "circle",
        color: "teal200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Caused by",
        showInverse: true,
        showInInspector: false
      },
      familyRef: {
        enabled: true,
        canDelete: false,
        id: "familyRef",
        key: null,
        label: "Family",
        types: ["character"],
        icon: "circle",
        color: "pastelPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: null,
        label: "Relates to",
        types: [
          "genericNode",
          "event",
          "scene",
          "flashback",
          "backstory",
          "character",
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "storyArc",
          "narrativeFolder",
          "book"
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: [
      "witnessRef",
      "participantRef",
      "storyArcRef",
      "locationRef",
      "protagonistRef",
      "antagonistRef",
      "bookRef",
      "birthplaceRef",
      "familyRef",
      "friendRef",
      "associateRef",
      "causesRef",
      "seeAlsoRef",
      REFERENCE_RELATES_TO
    ]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Decade,
    zoomTo: TimelineZoomLimits.Day,
    showTypes: ["event", "flashback", "scene", "backstory"]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultFictionNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.CONTINUOUS_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  }
};
